import { Check, Minus } from "lucide-react";
import { COMPARE_SECTIONS, NEW_PLANS, type CompareCell, type NewPlanId } from "@/content/pricing/plans";
import { formatUsd, type PricingCopy, type PricingLocale } from "@/content/pricing";
import { cellText } from "@/lib/pricing/compareCell";

// "Compare every feature" on /pricing-2026 (#compare-features): every Studio feature, plan by plan. Rows and
// which plan has what come from plans.ts COMPARE_SECTIONS (built from the real gating); the words from the copy's
// pricing.compare.
//
// One real <table> at every width: a caption, a column header per plan (sticky under the fixed site header as you
// scroll), a row group per section (th scope="rowgroup") and a row header per feature, so screen readers announce
// "Pro, Visitor analytics, Included". On phones it stays a four-column table: labels wrap, notes sit under them,
// and a value that's the same on every plan spans the three plan columns, so nothing scrolls sideways.

const PLAN_IDS: NewPlanId[] = ["free", "pro", "max"];

type Copy = PricingCopy["pricing"]["compare"];

const same = (a: CompareCell, b: CompareCell) => JSON.stringify(a) === JSON.stringify(b);

const Mark = ({ on, c }: { on: boolean; c: Copy }) =>
  on ? (
    <>
      <Check size={18} strokeWidth={2.6} className="mx-auto text-hawk-orange" aria-hidden="true" />
      <span className="sr-only">{c.included}</span>
    </>
  ) : (
    <>
      <Minus size={16} className="mx-auto text-muted-foreground/60" aria-hidden="true" />
      <span className="sr-only">{c.notIncluded}</span>
    </>
  );

const CompareFeatures = ({ loc }: { loc: PricingLocale }) => {
  const { copy, numberLocale } = loc;
  const c = copy.pricing.compare;
  const plans = PLAN_IDS.map((id) => NEW_PLANS.find((p) => p.id === id)!);
  const cellCls = (id: NewPlanId) => `px-1 py-3 text-center align-middle sm:px-3 ${id === "pro" ? "bg-hawk-orange/[0.04]" : ""}`;

  return (
    <section id="compare-features" className="scroll-mt-24 py-20" aria-labelledby="compare-features-heading">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <h2 id="compare-features-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
            {c.heading}
          </h2>
          <p className="text-muted-foreground">{c.subtitle}</p>
        </div>

        <table className="mx-auto w-full max-w-5xl table-fixed border-separate border-spacing-0 text-sm" data-testid="compare-features">
          <caption className="sr-only">{c.heading}</caption>
          <colgroup>
            <col className="w-[40%] sm:w-[46%]" />
            <col className="w-[20%] sm:w-[18%]" />
            <col className="w-[20%] sm:w-[18%]" />
            <col className="w-[20%] sm:w-[18%]" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="sticky top-[72px] z-10 md:top-[68px] border-b-2 border-border bg-background px-2 py-3 text-left align-bottom text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-4">
                {c.featureColumn}
              </th>
              {plans.map((plan) => (
                <th
                  key={plan.id}
                  scope="col"
                  className={`sticky top-[72px] z-10 md:top-[68px] border-b-2 bg-background px-1 py-3 text-center align-bottom sm:px-3 ${
                    plan.featured ? "border-hawk-orange" : "border-border"
                  }`}
                >
                  <span className="block font-display text-base font-bold sm:text-lg">{plan.name}</span>
                  <span className="block text-xs font-normal text-muted-foreground">
                    {formatUsd(plan.priceUsd, numberLocale)}
                    {copy.common.perMonth}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          {COMPARE_SECTIONS.map((section) => (
            <tbody key={section.id}>
              <tr>
                <th
                  scope="rowgroup"
                  colSpan={4}
                  className="border-b border-border bg-secondary/40 px-2 pb-2 pt-6 text-left font-display text-base font-bold sm:px-4"
                >
                  {c.sections[section.id]}
                </th>
              </tr>
              {section.rows.map((row) => {
                const label = c.rows[row.id as keyof Copy["rows"]];
                const cells = PLAN_IDS.map((id) => row.cells[id]);
                // A value that's the same on every plan is shown once, across the three plan columns.
                const spanAll = typeof cells[0] !== "boolean" && cells.every((x) => same(x, cells[0]));
                return (
                  <tr key={row.id} data-row={row.id}>
                    <th scope="row" className="border-b border-border px-2 py-3 text-left align-middle font-medium sm:px-4">
                      {label.label}
                      {label.note && <span className="mt-0.5 block text-xs font-normal text-muted-foreground [overflow-wrap:anywhere]">{label.note}</span>}
                    </th>
                    {spanAll ? (
                      <td colSpan={3} className="border-b border-border px-1 py-3 text-center align-middle font-semibold sm:px-3">
                        {cellText(cells[0], copy, numberLocale)}
                      </td>
                    ) : (
                      PLAN_IDS.map((id, i) => (
                        <td key={id} className={`border-b border-border font-semibold ${cellCls(id)}`}>
                          {typeof cells[i] === "boolean" ? <Mark on={cells[i] as boolean} c={c} /> : cellText(cells[i], copy, numberLocale)}
                        </td>
                      ))
                    )}
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
};

export default CompareFeatures;
